import React, { useState, useRef, useEffect } from 'react';
import { Menu, LogIn, UserPlus, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

const THEME_COLORS = {
  light: '#3b82f6',
  dark: '#1f2937',
  sports: '#16a34a',
  ocean: '#0891b2',
  sunset: '#ea580c',
  purple: '#9333ea',
  darkGreen: '#059669',
  darkBlue: '#2563eb',
  darkPurple: '#9333ea',
  darkOrange: '#ea580c',
};

export default function GuestMenuDropdown({ buttonClassName }) {
  const navigate = useNavigate();
  const { theme, themes, changeTheme, currentTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isThemeSubmenuOpen, setIsThemeSubmenuOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setIsThemeSubmenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const close = () => { setIsOpen(false); setIsThemeSubmenuOpen(false); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName ?? `p-2 rounded-lg ${theme.cardHover}`}
        title="Menu"
      >
        <Menu className={`w-6 h-6 ${buttonClassName ? 'text-white' : theme.textSecondary}`} />
      </button>

      {isOpen && (
        <div className={`absolute right-0 mt-2 w-56 ${theme.card} rounded-lg ${theme.shadow} border ${theme.border} z-[200]`}>
          <div className="py-1">
            <button
              onClick={() => { navigate('/signin'); close(); }}
              className={`flex items-center w-full px-4 py-3 text-sm ${theme.textSecondary} ${theme.cardHover}`}
            >
              <LogIn className="w-5 h-5 mr-3" />
              Sign In
            </button>

            <button
              onClick={() => { navigate('/signup'); close(); }}
              className={`flex items-center w-full px-4 py-3 text-sm ${theme.textSecondary} ${theme.cardHover}`}
            >
              <UserPlus className="w-5 h-5 mr-3" />
              Sign Up
            </button>

            <div className={`my-1 border-t ${theme.border}`} />

            <button
              onClick={() => setIsThemeSubmenuOpen(!isThemeSubmenuOpen)}
              className={`flex items-center w-full px-4 py-3 text-sm ${theme.textSecondary} ${theme.cardHover} justify-between`}
            >
              <span className="flex items-center">
                <div className="w-5 h-5 rounded-full mr-3 bg-gradient-to-r from-blue-500 to-purple-600" />
                Themes
              </span>
              <ChevronRight className={`w-4 h-4 transition-transform ${isThemeSubmenuOpen ? 'rotate-90' : ''}`} />
            </button>

            {isThemeSubmenuOpen && (
              <div className={`border-t ${theme.border} bg-opacity-50 ${theme.backgroundSecondary}`}>
                {themes.map((themeOption) => (
                  <button
                    key={themeOption.key}
                    onClick={() => { changeTheme(themeOption.key); close(); }}
                    className={`flex items-center w-full px-8 py-2 text-sm ${theme.cardHover} ${
                      currentTheme === themeOption.key
                        ? `${theme.primary.includes('bg-blue') ? 'bg-blue-50 text-blue-600' : theme.primary}`
                        : theme.textSecondary
                    }`}
                  >
                    <div
                      className="w-3 h-3 rounded-full mr-3"
                      style={{ background: THEME_COLORS[themeOption.key] ?? '#3b82f6' }}
                    />
                    {themeOption.name}
                    {currentTheme === themeOption.key && <span className="ml-auto text-blue-600">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
